import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Search,
  MapPin,
  Calendar,
  Users,
  Route,
  Building,
  User,
  Shield,
  Star,
  TrendingUp,
  DollarSign,
  CarFront,
  Tent,
} from 'lucide-react';
import { isMobile, isTablet } from '../../../shared/utils/deviceUtils';

type UserType = 'traveller' | 'operator' | 'transporter';

const userTypes = [
  {
    id: 'traveller' as UserType,
    title: 'Traveller',
    icon: Tent,
    description: 'Find and book amazing safari experiences',
  },
  {
    id: 'operator' as UserType,
    title: 'Tour Operator',
    icon: Building,
    description: 'List and manage your safari packages',
  },
  {
    id: 'transporter' as UserType,
    title: 'Transporter',
    icon: CarFront,
    description: 'Offer transportation services and earn money',
  },
];

const travellerFeatures = [
  {
    icon: Search,
    title: 'Discover Safaris',
    description: 'Browse thousands of safari packages from trusted operators',
  },
  {
    icon: MapPin,
    title: 'Find Destinations',
    description:
      'Explore popular destinations like Serengeti, Ngorongoro, and more',
  },
  {
    icon: Calendar,
    title: 'Easy Booking',
    description:
      'Book your dream safari with secure payment and instant confirmation',
  },
  {
    icon: Star,
    title: 'Verified Reviews',
    description: 'Read authentic reviews from fellow travelers',
  },
];

const operatorFeatures = [
  {
    icon: Building,
    title: 'List Your Packages',
    description:
      'Create and publish your safari packages to reach thousands of travelers',
  },
  {
    icon: Users,
    title: 'Manage Bookings',
    description: 'Handle reservations, payments, and customer communications',
  },
  {
    icon: TrendingUp,
    title: 'Grow Your Business',
    description: 'Access analytics and insights to optimize your offerings',
  },
  {
    icon: Shield,
    title: 'Trusted Platform',
    description: 'Build credibility with our verification and review system',
  },
];

const transporterFeatures = [
  {
    icon: CarFront,
    title: 'List Your Vehicles',
    description: 'Showcase your fleet and transportation services',
  },
  {
    icon: Route,
    title: 'Create Trips',
    description: 'Post available trips and routes for travelers to book',
  },
  {
    icon: DollarSign,
    title: 'Earn Money',
    description:
      'Generate income by providing reliable transportation services',
  },
  {
    icon: Users,
    title: 'Connect with Travelers',
    description: 'Direct connection with travelers looking for transportation',
  },
];

export const UserTypeSection: React.FC = () => {
  const [activeTab, setActiveTab] = useState<UserType>('traveller');
  const mobile = isMobile();
  const tablet = isTablet();
  const navigate = useNavigate();

  const getFeatures = () => {
    switch (activeTab) {
      case 'traveller':
        return travellerFeatures;
      case 'operator':
        return operatorFeatures;
      case 'transporter':
        return transporterFeatures;
      default:
        return travellerFeatures;
    }
  };

  const getDescription = () => {
    switch (activeTab) {
      case 'traveller':
        return 'Discover and book unforgettable safari experiences with our curated selection of packages from trusted tour operators. Find your perfect adventure and create memories that last a lifetime.';
      case 'operator':
        return 'Expand your reach and grow your safari business by listing your packages on our platform. Connect with thousands of travelers and manage your bookings efficiently.';
      case 'transporter':
        return 'Monetize your vehicles by offering transportation services to safari travelers. Create trips, connect with customers, and earn money while helping others explore Africa.';
      default:
        return '';
    }
  };

  const getActionButton = () => {
    switch (activeTab) {
      case 'traveller':
        return {
          text: 'Start Exploring',
          action: () => navigate('/safaris'),
          variant: 'primary' as const,
        };
      case 'operator':
        return {
          text: 'List Your Packages',
          action: () => navigate('/register?operatorType=tour_operator'),
          variant: 'secondary' as const,
        };
      case 'transporter':
        return {
          text: 'Start Earning',
          action: () => navigate('/register?operatorType=transporter'),
          variant: 'secondary' as const,
        };
      default:
        return {
          text: 'Get Started',
          action: () => navigate('/'),
          variant: 'primary' as const,
        };
    }
  };

  const actionButton = getActionButton();

  return (
    <section
      className={`${mobile ? 'py-16' : tablet ? 'py-20' : 'py-24'} bg-background-primary`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12 sm:mb-16">
          <h2
            className={`${mobile ? 'text-3xl' : tablet ? 'text-4xl' : 'text-4xl'} font-bold text-text-primary mb-4`}
          >
            Who Are You?
          </h2>
          <p
            className={`${mobile ? 'text-base' : 'text-lg'} text-text-secondary max-w-2xl mx-auto`}
          >
            Choose your path and discover how we can help you achieve your
            safari goals
          </p>
        </div>

        {/* Tab Navigation */}
        <div
          className={`flex ${mobile ? 'flex-col' : 'flex-wrap'} justify-center items-center gap-3 sm:gap-4 mb-8 sm:mb-12`}
        >
          {userTypes.map(type => {
            const Icon = type.icon;
            const isActive = activeTab === type.id;

            return (
              <button
                key={type.id}
                onClick={() => setActiveTab(type.id)}
                className={`flex items-center gap-3 ${mobile ? 'px-4 py-3' : 'px-6 py-4'} rounded-xl transition-all duration-300 font-medium ${
                  isActive
                    ? 'bg-primary-500 text-white shadow-primary transform scale-105'
                    : 'bg-white text-text-secondary hover:bg-gray-50 shadow-soft hover:shadow-medium'
                }`}
              >
                <Icon
                  className={`${mobile ? 'w-4 h-4' : 'w-5 h-5'} ${isActive ? 'text-white' : 'text-primary-500'}`}
                />
                <div className="text-left">
                  <div className={`${mobile ? 'text-sm' : 'font-semibold'}`}>
                    {type.title}
                  </div>
                  <div
                    className={`${mobile ? 'text-xs' : 'text-xs'} ${isActive ? 'text-white/80' : 'text-text-tertiary'}`}
                  >
                    {type.description}
                  </div>
                </div>
              </button>
            );
          })}
        </div>

        {/* Content */}
        <div
          className={`grid ${mobile ? 'grid-cols-1 gap-8' : tablet ? 'grid-cols-1 lg:grid-cols-2 gap-12' : 'grid-cols-1 lg:grid-cols-2 gap-16'} items-start justify-items-start max-w-6xl mx-auto`}
        >
          {/* Left: Features */}
          <div className="text-left p-4 sm:p-6 lg:p-8">
            <h3
              className={`${mobile ? 'text-2xl' : tablet ? 'text-3xl' : 'text-3xl'} font-bold text-text-primary mb-4 sm:mb-6`}
            >
              {activeTab === 'traveller' && 'Find Your Perfect Safari'}
              {activeTab === 'operator' && 'Grow Your Safari Business'}
              {activeTab === 'transporter' && 'Monetize Your Vehicles'}
            </h3>

            <p
              className={`${mobile ? 'text-base' : 'text-lg'} text-text-secondary mb-6 sm:mb-8 leading-relaxed`}
            >
              {getDescription()}
            </p>

            <div
              className={`grid ${mobile ? 'grid-cols-1 gap-4' : tablet ? 'grid-cols-1 sm:grid-cols-2 gap-5' : 'grid-cols-1 sm:grid-cols-2 gap-6'} justify-items-start`}
            >
              {getFeatures().map((feature, index) => {
                const Icon = feature.icon;
                return (
                  <div key={index} className="flex items-start gap-3 sm:gap-4">
                    <div
                      className={`${mobile ? 'w-10 h-10' : 'w-12 h-12'} bg-primary-100 rounded-xl flex items-center justify-center flex-shrink-0`}
                    >
                      <Icon
                        className={`${mobile ? 'w-5 h-5' : 'w-6 h-6'} text-primary-500`}
                      />
                    </div>
                    <div>
                      <h4
                        className={`${mobile ? 'text-sm' : 'font-semibold'} text-text-primary mb-1 sm:mb-2`}
                      >
                        {feature.title}
                      </h4>
                      <p
                        className={`${mobile ? 'text-xs' : 'text-sm'} text-text-secondary leading-relaxed`}
                      >
                        {feature.description}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Action Button */}
            <div className="mt-6 sm:mt-8 flex justify-start">
              <button
                onClick={actionButton.action}
                className={`inline-flex items-center gap-2 ${mobile ? 'px-6 py-3 text-sm' : 'px-8 py-4'} rounded-xl font-medium transition-all duration-200 shadow-soft hover:shadow-large transform hover:scale-105 ${
                  actionButton.variant === 'primary'
                    ? 'bg-primary-500 text-white hover:bg-primary-600'
                    : 'bg-white text-primary-500 border-2 border-primary-500 hover:bg-primary-50'
                }`}
              >
                {actionButton.text}
                <svg
                  className={`${mobile ? 'w-3 h-3' : 'w-4 h-4'}`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 5l7 7-7 7"
                  />
                </svg>
              </button>
            </div>
          </div>

          {/* Right: Visual */}
          <div className={`relative ${mobile ? 'mt-0' : ''} flex justify-start p-4 sm:p-6 lg:p-8`}>
            <div className="relative rounded-xl overflow-hidden shadow-large w-full h-[500px]">
              <img
                src={
                  activeTab === 'traveller'
                    ? 'https://images.pexels.com/photos/4553618/pexels-photo-4553618.jpeg'
                    : activeTab === 'operator'
                      ? 'https://images.pexels.com/photos/4577791/pexels-photo-4577791.jpeg'
                      : 'https://images.pexels.com/photos/4577801/pexels-photo-4577801.jpeg'
                }
                alt={`${activeTab} experience`}
                className="w-full h-full object-cover transition-all duration-500"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent"></div>

              {/* Overlay Content */}
              <div className="absolute bottom-4 sm:bottom-6 left-4 sm:left-6 right-4 sm:right-6 text-white">
                <div className="flex items-center gap-2 sm:gap-3 mb-1 sm:mb-2">
                  {(() => {
                    const Icon =
                      userTypes.find(t => t.id === activeTab)?.icon || User;
                    return (
                      <Icon className={`${mobile ? 'w-5 h-5' : 'w-6 h-6'}`} />
                    );
                  })()}
                  <h4
                    className={`${mobile ? 'text-lg' : 'text-xl'} font-bold capitalize`}
                  >
                    {activeTab} Experience
                  </h4>
                </div>
                <p
                  className={`${mobile ? 'text-xs' : 'text-sm'} text-white/90`}
                >
                  {activeTab === 'traveller' &&
                    'Discover amazing wildlife and landscapes'}
                  {activeTab === 'operator' &&
                    'Connect with travelers worldwide'}
                  {activeTab === 'transporter' &&
                    'Earn while helping others explore'}
                </p>
              </div>
            </div>

            {/* Floating Stats */}
            <div
              className={`absolute ${mobile ? '-top-2 -right-2' : '-top-4 -right-4'} bg-white rounded-xl ${mobile ? 'p-3' : 'p-4'} shadow-large`}
            >
              <div className="text-center">
                <div
                  className={`${mobile ? 'text-lg' : 'text-2xl'} font-bold text-primary-500`}
                >
                  {activeTab === 'traveller' && '15K+'}
                  {activeTab === 'operator' && '500+'}
                  {activeTab === 'transporter' && '200+'}
                </div>
                <div
                  className={`${mobile ? 'text-xs' : 'text-xs'} text-text-secondary`}
                >
                  {activeTab === 'traveller' && 'Happy Travelers'}
                  {activeTab === 'operator' && 'Tour Operators'}
                  {activeTab === 'transporter' && 'Active Transporters'}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
